import { useState, useEffect } from 'react';

import { fetchArchivedVideo } from 'api/session';
import { useAuthContext } from 'store';

export type VideoItem = {
  _id: string;
  label: string;
  start: number;
  end: number;
  is_archived?: boolean;
  type?: 'mp4';
  query?: MorressierVideoItem['query'];
  video_url?: string;
};

export const parseFetchedSessions =
  (presets: MorressierVideoItem[]) =>
  ({ video_url }: { video_url: string }) =>
    presets
      .filter(p => p.is_assigned)
      .map(p => {
        if (p.is_archived) {
          return { ...p, video_url };
        }
        return p;
      })
      .reduce((acc, curr, index) => {
        const duration = curr.end - curr.start;
        let timecode = 0;
        const prev = acc[acc.length - 1];
        if (prev) {
          timecode = prev.timecode + prev.duration;
        }
        return [
          ...acc,
          {
            ...curr,
            id: `ID-${index}`,
            title: `# ${index}`,
            timecode,
            duration,
          },
        ];
      }, [] as (MorressierVideoItem & { timecode: number; duration: number })[]);

export const resolveTotalTime = (chunks: { start: number; end: number }[]) => () => {
  const totalTime = chunks.reduce((acc, curr) => acc + curr.end - curr.start, 0);
  return totalTime;
};

export const resolveSeekPosition = chunks => (seekPercent: number) => {
  const chunksWidthTime = chunks.reduce((acc, curr) => {
    const prev = acc[acc.length - 1];
    const prevEnd = prev ? prev.total + prev.prevEnd : 0;

    return [
      ...acc,
      {
        start: curr.start,
        end: curr.end,
        prevEnd,
        total: curr.end - curr.start,
      },
    ];
  }, []);

  const totalTime = chunks.reduce((acc, curr) => acc + curr.end - curr.start, 0);

  const timePosition = (totalTime * seekPercent) / 100;

  const matchingChunkIndex = chunksWidthTime.findIndex(
    c => c.prevEnd <= timePosition && c.prevEnd + c.total >= timePosition,
  );
  const matchingChunk = chunksWidthTime[matchingChunkIndex];
  return {
    index: matchingChunkIndex,
    chunkTime: matchingChunk.start + timePosition - matchingChunk.prevEnd,
  };
};

export const resolveOverallTime = chunks => (chunkIndex: number, chunkTime: number) => {
  const chunksWidthTime = chunks.reduce((acc, curr) => {
    const prev = acc[acc.length - 1];
    const prevEnd = prev ? prev.total + prev.prevEnd : 0;

    return [
      ...acc,
      {
        start: curr.start,
        end: curr.end,
        prevEnd,
        total: curr.end - curr.start,
      },
    ];
  }, []);

  const timeChunk = chunksWidthTime[chunkIndex];

  const timeProgress =
    Math.min(Math.max(chunkTime, timeChunk.start), timeChunk.end) - timeChunk.start;

  const chunkEndInSeconds = parseInt(timeChunk.end.toString(), 10);
  const totalChunkInSeconds = parseInt(chunkTime.toString(), 10);
  return {
    playbackTime: timeChunk.prevEnd + timeProgress,
    isComplete: chunkEndInSeconds <= totalChunkInSeconds,
  };
};

export const useSessionPresentation = (
  id: string,
  videoSession: MorressierVideoSession | null,
  archiveStreamUrl?: string,
) => {
  const [sections, setSections] = useState<MorressierVideoItem[]>([]);
  const { state } = useAuthContext();

  useEffect(() => {
    const fetch = async (presentation?: MorressierVideoSessionPresentation) => {
      if (presentation) {
        const archived = presentation.videos.filter(
          video => video.is_archived && video.is_assigned && video.query,
        );
        if (archived.length === 0) {
          const fetchedSections = parseFetchedSessions(presentation.videos)({
            video_url: '',
          });
          setSections(fetchedSections);
        } else {
          const archivesUrls = await Promise.all(
            archived.map(video => fetchArchivedVideo(video?.query?.session)),
          );

          if (archivesUrls) {
            archivesUrls.forEach(url => {
              if (url) {
                const fetchedSections = parseFetchedSessions(presentation.videos)({
                  video_url: archiveStreamUrl && archiveStreamUrl !== '' ? archiveStreamUrl : url,
                });
                setSections(fetchedSections);
              }
            });
          }
        }
      }
    };

    if (videoSession && id) {
      const selectedPresentation = videoSession.presentations.find(
        presentation => presentation.presentation_id === id,
      );
      fetch(selectedPresentation);
    }
  }, [videoSession, id, state?.authInfo?.access_token]);

  return { sections };
};
